import { Box, Divider, Stack, styled } from '@mui/material';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { InformationOutlinedIcon, theme } from '@synergy/theme';
import { PoolBanner, PoolTooltip, PoolTypography } from '@synergy/components-next';
import { PoolMath, getConditionTotals, getCurrencySymbol } from '@synergy/utils';
import { ReactElement, useEffect, useMemo } from 'react';
import { getUnitTranslationId } from '@synergy/models';
import { useAppDispatch } from '@synergy/admin/app-settings/data-access';
import { useFormikContext } from 'formik';

import { BonusSettlementSupplierDetailFormValues } from '../models/bonus-settlement-supplier-detail-form-values';
import { bestLocaleSelector } from '../../../../common/selectors/locale-selectors';
import { bonusSettlementsSupplierDetailSelector } from '../store/bonus-settlements-supplier-detail-selector';
import { defaultCurrencySelector } from '../../../../common/selectors/currency-selectors';
import { resetBonusSettlementsSupplierDetail } from '../store/bonus-settlements-supplier-detail-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';

const StyledRow = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    paddingBlock: theme.spacing(1),
}));

export const BonusSettlementSupplierDetailSidebar = (): ReactElement => {
    const bonusSettlementSupplierDetail = useAppSelector(bonusSettlementsSupplierDetailSelector);
    const { values } = useFormikContext<BonusSettlementSupplierDetailFormValues>();
    const intl = useIntl();
    const locale = useAppSelector(bestLocaleSelector);
    const defaultCurrency = useAppSelector(defaultCurrencySelector);
    const dispatch = useAppDispatch();
    const totalNet = useMemo(() => {
        return values.customerBonusSettlements.reduce((acc, customerBonusSettlement) => {
            return acc + customerBonusSettlement.actualBonus;
        }, 0);
    }, [values.customerBonusSettlements]);

    const bonusTaxPercentage = bonusSettlementSupplierDetail?.bonusTaxPercentage ?? 0;
    const calculatedTax = PoolMath.round(totalNet * (bonusTaxPercentage / 100));
    const total = totalNet + calculatedTax;

    useEffect(() => {
        return () => {
            // Reset your state here when the component unmounts
            dispatch(resetBonusSettlementsSupplierDetail());
        };
    }, []);

    return (
        // The dock supplies the frame; this is only what used to sit inside the
        // sidebar's own chrome.
        <Stack typography="body2" gap={3}>
            <Stack gap={2}>
                <PoolTypography typography="subtitle1" translationId="app.common.overallOverview" />

                <Box>
                    <Stack divider={<Divider />}>
                        {bonusSettlementSupplierDetail?.bonusConditions.map(condition => {
                            const { mode, currency, unit } = condition.bonusCondition;

                            const { value: conditionValueSum, bonus: conditionBonus } = getConditionTotals(
                                condition,
                                values.customerBonusSettlements
                            );

                            return (
                                <StyledRow key={condition.bonusCondition.id}>
                                    <Stack>
                                        <FormattedMessage
                                            id="app.bonusSettlements.supplierDetail.detail.condition"
                                            values={{
                                                currency: getCurrencySymbol(currency || defaultCurrency, locale),
                                                value: intl.formatNumber(conditionValueSum, {
                                                    maximumFractionDigits: 2,
                                                }),
                                                mode,
                                                unit: unit && intl.formatMessage({ id: getUnitTranslationId(unit) }),
                                            }}
                                        />
                                        <Stack direction="row" alignItems="center" gap={0.5} color="grey.800">
                                            <FormattedMessage
                                                id={`app.bonusSettlementSupplier.bonusSettlementCondition.${mode}`}
                                                values={{
                                                    mode,
                                                    quantity: condition.bonusCondition.quantity || 1,
                                                    percentage: intl.formatNumber(
                                                        (condition.bonusCondition.percentage ?? 0) * 100,
                                                        {
                                                            maximumFractionDigits: 2,
                                                        }
                                                    ),
                                                    price: intl.formatNumber(condition.bonusCondition.price ?? 0, {
                                                        maximumFractionDigits: 2,
                                                        minimumFractionDigits: 2,
                                                    }),
                                                    currency: getCurrencySymbol(currency || defaultCurrency, locale),
                                                    unit:
                                                        unit &&
                                                        intl.formatMessage({
                                                            id: getUnitTranslationId(unit),
                                                        }),
                                                }}
                                            />
                                            {condition.bonusCondition.title && (
                                                <PoolTooltip title={condition.bonusCondition.title}>
                                                    <InformationOutlinedIcon
                                                        htmlColor={theme.palette.grey[800]}
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                </PoolTooltip>
                                            )}
                                        </Stack>
                                    </Stack>

                                    <FormattedNumber
                                        value={conditionBonus}
                                        style="currency"
                                        currency={currency || defaultCurrency}
                                        minimumFractionDigits={2}
                                        maximumFractionDigits={2}
                                    />
                                </StyledRow>
                            );
                        })}
                    </Stack>

                    <Divider sx={{ backgroundColor: theme => theme.palette.text.primary }} />

                    <Stack divider={<Divider />}>
                        <StyledRow sx={{ fontWeight: 600 }}>
                            <Box>
                                <FormattedMessage id="app.bonusSettlements.supplierDetail.detail.totalNet" />
                            </Box>
                            <Box>
                                <FormattedNumber
                                    value={totalNet}
                                    style="currency"
                                    currency={defaultCurrency}
                                    maximumFractionDigits={2}
                                />
                            </Box>
                        </StyledRow>

                        <StyledRow>
                            <Box>
                                <FormattedMessage
                                    id="app.bonusSettlements.supplierDetail.detail.tax"
                                    values={{ value: bonusTaxPercentage }}
                                />
                            </Box>
                            <Box>
                                <FormattedNumber
                                    value={calculatedTax}
                                    style="currency"
                                    currency={defaultCurrency}
                                    minimumFractionDigits={2}
                                    maximumFractionDigits={2}
                                />
                            </Box>
                        </StyledRow>
                    </Stack>

                    <Divider sx={{ backgroundColor: theme => theme.palette.text.primary }} />

                    <StyledRow sx={{ fontWeight: 600 }}>
                        <Box>
                            <FormattedMessage id="app.bonusSettlements.supplierDetail.detail.total" />
                        </Box>
                        <Box>
                            <FormattedNumber
                                value={total}
                                style="currency"
                                currency={defaultCurrency}
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                            />
                        </Box>
                    </StyledRow>
                </Box>
            </Stack>

            <PoolBanner variant="outlined">
                <FormattedMessage
                    id="app.bonusSettlements.supplierDetail.detail.banner"
                    values={{ value: bonusSettlementSupplierDetail?.billingModality }}
                />
            </PoolBanner>
        </Stack>
    );
};
